import Box from '@mui/material/Box';
import React from 'react';

// By module path: the `hooks/tenants` barrel carries every tenant API hook.
import useTenantFeatures from '../../hooks/tenants/useTenantFeatures';
import { isMarketplacePurchaseReturn } from '../Payments/marketplacePurchaseReturnIdentity';
import { useGetUrlParams } from '../../hooks/useGetUrlParams';
import TenantFeatureRoute from './TenantFeatureRoute';

// Loaded lazily, not as a static import: this wrapper is part of the router's
// eager import graph (`routes.tsx`), and the handler would drag the context
// barrel, the payment API clients and the acquisition store into it. It is
// needed only for a switched-off visit that carries a checkout return.
const LazyMarketplacePurchaseReturn = React.lazy(() =>
  import('../Payments/MarketplacePurchaseReturn').then((module) => ({
    default: module.MarketplacePurchaseReturn,
  })),
);

export interface MarketplaceFeatureRouteProps {
  children: React.ReactNode;
}

/**
 * `/marketplace` with the tenant's marketplace switch.
 *
 * Checkout returns to `/marketplace?purchase=…`, and the confirmation
 * (`MarketplacePurchaseReturn`) lives on that page. A buyer whose checkout
 * started before the switch went off would otherwise be redirected away with
 * the query, never learning whether the purchase landed (purchases and bought
 * content stay while the marketplace is off).
 *
 * So with the switch off AND a return the handler can act on, only that
 * handler renders — never the page. It toasts the outcome and clears its
 * markers; the next render has no return left and the wrapper redirects to `/`
 * as for any switched-off visit. Any other off visit redirects at once.
 *
 * Platform users pass through `useTenantFeatures` (the switch always answers
 * on for them), so they get the page and its own handler.
 */
const MarketplaceFeatureRoute = ({
  children,
}: MarketplaceFeatureRouteProps) => {
  const { isLoading, isOn } = useTenantFeatures();
  const [params] = useGetUrlParams();

  if (
    !isLoading &&
    !isOn('marketplace') &&
    isMarketplacePurchaseReturn(params)
  ) {
    return (
      <Box p={3}>
        <React.Suspense fallback={null}>
          <LazyMarketplacePurchaseReturn />
        </React.Suspense>
      </Box>
    );
  }

  return (
    <TenantFeatureRoute feature="marketplace">{children}</TenantFeatureRoute>
  );
};

export default MarketplaceFeatureRoute;
